import { useRouter } from 'next/router';
import { createContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getV3Configuration } from '@/api/configuration';

export const CurrencyContext = createContext({
  currency: 'EUR',
  formatter: new Intl.NumberFormat('el-GR', { style: 'currency', currency: 'EUR' }),
  symbol: '€',
  setCurrency: val => {},
});

export function CurrencyProvider({ children }) {
  const { locale } = useRouter();
  const [currency, setCurrency] = useState('EUR');
  const [formatterLength, setFormatterLength] = useState(2);

  const { data: currencyConfig } = useQuery({
    queryKey: ['config', locale],
    queryFn: ({ queryKey }) => getV3Configuration(queryKey[1]),
    select: data => data.currency,
    staleTime: 1000 * 60 * 60 * 24,
  });

  const minorUnitFactor = currencyConfig?.minorUnitFactor;

  useEffect(() => {
    if (currencyConfig) {
      setCurrency(currencyConfig.default);
      if (minorUnitFactor === 1000) setFormatterLength(3);
      else if (minorUnitFactor === 1) setFormatterLength(0);
      else setFormatterLength(2);
    }
  }, [currencyConfig, minorUnitFactor]);

  const { formatter, symbol } = useMemo(() => {
    // 1. Create the internal native formatter
    const fmt = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: formatterLength,
      maximumFractionDigits: formatterLength,
    });

    // 2. Extract symbol exactly like the old provider
    const symbolPart = fmt.formatToParts(0).find(part => part.type === 'currency');
    const extractedSymbol = symbolPart ? symbolPart.value : '';

    // 3. Return a combined object that mimics the old formatter interface
    return {
      symbol: extractedSymbol,
      formatter: {
        ...fmt, // Spread properties if any are enumerable
        format: amount => {
          let adjustedAmount = amount;
          switch (minorUnitFactor) {
            case 1000:
              adjustedAmount = amount / 10;
              break;
            case 1:
              adjustedAmount = Math.round(amount * 100);
              break;
            default:
              break;
          }
          return fmt.format(adjustedAmount);
        },
        // Include formatToParts in case your components use it
        formatToParts: amount => fmt.formatToParts(amount),
      },
    };
  }, [currency, locale, formatterLength, minorUnitFactor]);

  return (
    <CurrencyContext.Provider value={{ currency, formatter, symbol, setCurrency }}>{children}</CurrencyContext.Provider>
  );
}
